import { ThemeEvents } from '@theme/events';

class CartIcon extends HTMLElement {
  constructor() {
    super();
    this.cartCount = null;
    this.addToCartBehavior = null;
    this.lastDrawerRefresh = 0;

    this.clickHandler = this.handleClick.bind(this);
    this.cartUpdatedHandler = this.onCartUpdated.bind(this);
    this.syncHandler = (event) => {
      this.#diag('pageshow ' + (event && event.type === 'pageshow' ? 'persisted=' + event.persisted : (event && event.type) || ''));
      this.ensureCartCountIsCorrect();
    };
    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible') this.ensureCartCountIsCorrect();
    };
  }

  connectedCallback() {
    this.addToCartBehavior = this.dataset.addToCartBehavior || 'open_cart';

    this.cartCount = this.querySelector('[data-ref="cart-count"]');

    this.addEventListener('click', this.clickHandler);
    document.addEventListener(ThemeEvents.cartUpdated, this.cartUpdatedHandler);
    window.addEventListener('pageshow', this.syncHandler);
    document.addEventListener('visibilitychange', this.visibilityHandler);
    document.addEventListener('prerenderingchange', this.syncHandler);

    try {
      if (window.location.search.includes('diag=1')) localStorage.setItem('msv-diag', '1');
      if (window.location.search.includes('diag=0')) localStorage.removeItem('msv-diag');
    } catch (e) {
      // sans stockage
    }
    this.#diag('page chargee (compteur HTML=' + (this.cartCount ? this.cartCount.textContent : '?') + ')');
    this.#showDiag();

    // Au chargement de chaque page : on aligne compteur et tiroir sur le vrai panier
    this.ensureCartCountIsCorrect();
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdated, this.cartUpdatedHandler);
    window.removeEventListener('pageshow', this.syncHandler);
    document.removeEventListener('visibilitychange', this.visibilityHandler);
    document.removeEventListener('prerenderingchange', this.syncHandler);
    this.removeEventListener('click', this.clickHandler);
  }

  getCurrentItemCount() {
    return parseInt(this.cartCount.textContent);
  }

  // ---- Mode test (actif seulement après ouverture de ?diag=1) ----
  #diagOn() {
    try {
      return localStorage.getItem('msv-diag') === '1';
    } catch (e) {
      return false;
    }
  }

  async #diag(label) {
    if (!this.#diagOn()) return;
    let real = '?';
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
      const cart = await response.json();
      real = cart.item_count;
    } catch (e) {
      real = 'erreur';
    }
    const drawer = document.querySelector('[data-ref="cart-drawer"]');
    const drawerQty = drawer ? this.#drawerQuantity(drawer) : '-';
    const shown = this.cartCount ? this.cartCount.textContent : '?';
    const time = new Date().toLocaleTimeString('fr-FR');
    const line = time + ' ' + label + ' | reel=' + real + ' | icone=' + shown + ' | tiroir=' + drawerQty;
    try {
      const lines = JSON.parse(sessionStorage.getItem('msv:diag') || '[]');
      lines.push(line);
      sessionStorage.setItem('msv:diag', JSON.stringify(lines.slice(-12)));
    } catch (e) {
      // sans stockage
    }
    this.#showDiag();
  }

  #showDiag() {
    if (!this.#diagOn()) return;
    let lines = [];
    try {
      lines = JSON.parse(sessionStorage.getItem('msv:diag') || '[]');
    } catch (e) {
      lines = [];
    }
    let box = document.getElementById('msv-diag');
    if (!box) {
      box = document.createElement('div');
      box.id = 'msv-diag';
      box.style.cssText = 'position:fixed;left:0;right:0;bottom:0;z-index:2147483647;background:#000;color:#0f0;font:11px/1.35 monospace;padding:6px;max-height:40vh;overflow:auto;white-space:pre-wrap;';
      box.addEventListener('click', () => box.remove());
      document.body.appendChild(box);
    }
    box.textContent = lines.join('\n');
  }

  onCartUpdated(event) {
    const { itemCount, source, sections } = event.detail.data;

    this.#diag('AJOUT source=' + source + ' compteur-annonce=' + itemCount + ' html-tiroir=' + (sections ? Object.keys(sections).join('+') : 'AUCUN'));
    setTimeout(() => this.#diag('+1s'), 1000);
    setTimeout(() => this.#diag('+4s'), 4000);

    if (typeof itemCount === 'number') {
      this.#setCount(itemCount);
    }

    if (this.addToCartBehavior === 'open_cart' && source !== 'cart-reservation-timer') {
      this.openCart();
    }

    setTimeout(() => {
      this.ensureCartCountIsCorrect();
    }, sections ? 500 : 100);
  }

  #setCount(count) {
    if (!this.cartCount) return;
    this.cartCount.textContent = count;
  }

  #drawerQuantity(cartDrawer) {
    let total = 0;
    cartDrawer.querySelectorAll('[data-ref="cart-item"]').forEach((item) => {
      const quantity = parseInt(item.dataset.quantity ?? '0', 10);
      total += Number.isNaN(quantity) ? 0 : quantity;
    });
    return total;
  }

  // Le compteur et le tiroir suivent toujours le vrai panier (serveur).
  async ensureCartCountIsCorrect() {
    if (!this.cartCount) return;
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) return;
      const cart = await response.json();
      if (typeof cart.item_count !== 'number') return;

      this.#setCount(cart.item_count);

      const cartDrawer = document.querySelector('[data-ref="cart-drawer"]');
      if (!cartDrawer) return;

      if (this.#drawerQuantity(cartDrawer) === cart.item_count) return;

      // Le tiroir ne correspond pas au vrai panier : on le recharge
      if (typeof cartDrawer.refresh === 'function' && Date.now() - this.lastDrawerRefresh > 2500) {
        this.lastDrawerRefresh = Date.now();
        await cartDrawer.refresh();
      }

      // Toujours différent : dernier recours, on recharge la page (au plus une fois toutes les 20 secondes)
      if (this.#drawerQuantity(cartDrawer) !== cart.item_count) {
        let last = 0;
        try {
          last = Number(sessionStorage.getItem('msv:cart-reload')) || 0;
        } catch (e) {
          last = 0;
        }
        if (Date.now() - last > 20000) {
          try {
            sessionStorage.setItem('msv:cart-reload', String(Date.now()));
          } catch (e) {
            return;
          }
          this.#diag('RECHARGEMENT automatique');
          window.location.reload();
        }
      }
    } catch (error) {
      console.error('cart-icon sync failed', error);
    }
  }

  handleClick() {
    this.openCart();
  }

  openCart() {
    const cartDrawer = document.querySelector('[data-ref="cart-drawer"]');
    if (cartDrawer) {
      cartDrawer.open();
    } else {
      window.location.href = Theme.routes.cart_url;
    }
  }
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
